import {
  Box,
  Button,
  Column,
  Columns,
  Heading,
  List,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';
// TODO: Evaluate whether we want to keep using zod
import type { z } from 'zod';

import { createDotNetLinkUrl } from 'src/utils';

import type { FormSchema } from './NewsletterSignUpForm.helpers';
import { useNewsletterSignUpFormState } from './NewsletterSignUpForm.hooks';

export function NewsletterSignUpForm() {
  const {
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    setEmail,
    formStatus,
    handleSubmit,
  } = useNewsletterSignUpFormState();

  if (formStatus.kind === 'success') {
    return (
      <Box padding="medium" background="brand" borderRadius="large">
        <Text>Thanks for subscribing to the SEEK Business newsletter</Text>
      </Box>
    );
  }

  const submitButtonSharedProps = {
    type: 'submit' as const,
    loading: formStatus.kind === 'loading',
    data: {
      htag: 'footer-subscribe-link',
    },
  };

  const hasValidationError = (field: keyof z.ZodFormattedError<FormSchema>) => {
    if (formStatus.kind !== 'validation-error') return false;
    const formattedErrors = formStatus.errors.format();
    return formattedErrors[field];
  };

  return (
    <Box padding="medium" background="brand" borderRadius="large">
      <form onSubmit={handleSubmit} noValidate>
        <Stack space="medium">
          <Heading level="3">Unlock expert business-buying advice</Heading>

          <Text tone="secondary">We’ll guide you from search to purchase.</Text>

          <Columns
            space={{ mobile: 'medium', tablet: 'small' }}
            collapseBelow="tablet"
            alignY="bottom"
          >
            <Column>
              <TextField
                id="footer-fistname"
                label="First name"
                value={firstName}
                onChange={(ev) => setFirstName(ev.currentTarget.value)}
                aria-describedby={ERROR_CONTAINER_ID}
                {...(hasValidationError('FirstName') && {
                  tone: 'critical' as const,
                })}
              />
            </Column>

            <Column>
              <TextField
                id="footer-lastname"
                label="Last name"
                value={lastName}
                onChange={(ev) => setLastName(ev.currentTarget.value)}
                aria-describedby={ERROR_CONTAINER_ID}
                {...(hasValidationError('LastName') && {
                  tone: 'critical' as const,
                })}
              />
            </Column>

            <Column>
              <TextField
                id="footer-email"
                type="email"
                label="Email"
                value={email}
                onChange={(ev) => setEmail(ev.currentTarget.value)}
                aria-describedby={ERROR_CONTAINER_ID}
                {...(hasValidationError('Email') && {
                  tone: 'critical' as const,
                })}
              />
            </Column>

            <Column>
              <Button tone="brandAccent" {...submitButtonSharedProps}>
                Subscribe
              </Button>
            </Column>
          </Columns>

          <Text
            size="small"
            tone="secondary"
            data={{
              testid: 'site-wide-footer-newsletter-signup-terms',
            }}
          >
            By subscribing, you agree to our{' '}
            <TextLink
              href={createDotNetLinkUrl('collection-notice')}
              target="_blank"
            >
              Collection Notice
            </TextLink>{' '}
            and{' '}
            <TextLink
              href={createDotNetLinkUrl('privacy-policy')}
              target="_blank"
            >
              Privacy Policy
            </TextLink>
            . You can unsubscribe at any time.
          </Text>

          {formStatus.kind === 'validation-error' && (
            <Box
              id={ERROR_CONTAINER_ID}
              paddingTop="small"
              data={{
                testid: 'site-wide-footer-newsletter-signup-errors',
              }}
            >
              <NewsletterSignUpFormErrors errors={formStatus.errors} />
            </Box>
          )}
        </Stack>
      </form>
    </Box>
  );
}

const ERROR_CONTAINER_ID = 'site-wide-footer-newsletter-signup-errors';

interface NewsletterSignUpFormErrorsProps {
  errors: z.ZodError<FormSchema>;
}

function NewsletterSignUpFormErrors(props: NewsletterSignUpFormErrorsProps) {
  return (
    <Box
      background="surface"
      padding="small"
      borderRadius="standard"
      marginBottom="small"
    >
      <List tone="critical">
        {props.errors.issues.map((error) => (
          <Text key={error.path + error.code}>{error.message}</Text>
        ))}
      </List>
    </Box>
  );
}
